import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { ITEM_DEFINITIONS, SLOT_LABELS } from '../data/items';
import { BUILDING_DEFINITIONS } from '../data/buildings';
import { ItemInstance, PERSONAL_SLOTS } from '../types/Item.types';
import { Construction } from '../systems/UpgradeManager';
import { ExpeditionManager } from '../systems/ExpeditionManager';
import { SfxManager } from '../utils/SfxManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { RARITY_COLORS } from '../ui/InventoryUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { LeaderboardClient, computeRunScore } from '../systems/LeaderboardClient';
import { PlayerProfile } from '../systems/PlayerProfile';

// === SCÈNE DE FIN DE RUN ===
// Victoire : transférer tout ce qu'on veut au coffre.
// Défaite  : sauver 1 seul item.
// Utilise InventoryManager comme source de vérité unique.

const HEADER_H = 85;

export class RunEndScene extends Phaser.Scene {
  // Référence nullable — nécessaire pour destroy() avant redessinage ET en shutdown
  private panel: ScrollablePanel | null = null;
  private run!: RunManager;
  private camp!: CampManager;
  private meta!: MetaManager;
  private sfx = new SfxManager();
  private victory = false;
  private savedItems: ItemInstance[] = [];
  private defeatSaved = false;
  private completedConstruction: Construction | null = null;
  private redirecting = false;
  private isProcessing = false;
  /** État de la soumission au leaderboard (affiché en bas du header) */
  private leaderboardStatus: 'idle' | 'sending' | 'sent' | 'failed' | 'declined' | 'no-api' = 'idle';
  private leaderboardRank: number | null = null;
  /** Garde anti-double-soumission (sinon drawUI() relancerait à chaque redessinage) */
  private leaderboardSubmitted = false;

  constructor() {
    super({ key: 'RunEndScene' });
  }

  init(data: { run: RunManager; victory: boolean }): void {
    this.run = data.run;
    this.camp = this.run.getCamp();
    this.meta = this.run.getMeta();
    this.victory = data.victory;
    this.savedItems = [];
    this.defeatSaved = false;
    this.redirecting = false;

    // Bug fix : appliquer les recrutements promis pendant le run (uniquement si victoire)
    // Évite que les survivants rencontrés rejoignent le camp en cas de permadeath.
    if (this.victory) {
      this.run.applyPendingRecruits();
    }

    // Terminer la construction en cours (si un upgrade était lancé)
    this.completedConstruction = this.camp.getUpgrades().completeConstruction();

    // Vérifier si une expédition idle était en cours → résoudre d'abord
    const expeditionMgr = new ExpeditionManager();
    if (expeditionMgr.isOnExpedition()) {
      const result = expeditionMgr.resolveExpedition(this.camp.getSurvivors());
      if (result) {
        this.redirecting = true;
        this.scene.start('ExpeditionResultScene', { run: this.run, result, victory: this.victory });
        return;
      }
    }
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    if (this.redirecting) return;
    this.drawUI();
    // Soumission leaderboard (victoire uniquement) — déclenchée une seule fois.
    if (this.victory && !this.leaderboardSubmitted) {
      this.leaderboardSubmitted = true;
      this.maybeSubmitToLeaderboard();
    }
  }

  /**
   * Logique de soumission au leaderboard :
   * 1. API non configurée → skip silencieux
   * 2. Pas de pseudo → skip silencieux
   * 3. Consentement jamais demandé → afficher popup opt-in
   * 4. Consentement 'accepted' → POST direct, update status à l'écran
   * 5. Consentement 'declined' → skip silencieux (status 'declined')
   */
  private maybeSubmitToLeaderboard(): void {
    if (!LeaderboardClient.isEnabled()) {
      this.leaderboardStatus = 'no-api';
      return;
    }
    if (!PlayerProfile.getPseudo()) {
      // Pas de pseudo (cas improbable — BootScene en demande un au 1er lancement)
      this.leaderboardStatus = 'declined';
      return;
    }
    const consent = LeaderboardClient.getConsent();
    if (consent === 'declined') {
      this.leaderboardStatus = 'declined';
      return;
    }
    if (consent === null) {
      this.showConsentPopup();
      return;
    }
    this.doSubmit();
  }

  /** Modale opt-in affichée à la 1re soumission. */
  private showConsentPopup(): void {
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.75)
      .setDepth(200).setInteractive();

    const boxW = Math.min(340, GAME_W - 30);
    const box = this.add.rectangle(GAME_CX, GAME_CY, boxW, 240, 0x1a1a2e)
      .setDepth(201).setStrokeStyle(2, 0x1abc9c);

    const title = this.add.text(GAME_CX, GAME_CY - 95, '🏆 Soumettre ton score ?', {
      fontSize: '15px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(202);

    const desc = this.add.text(GAME_CX, GAME_CY - 30,
      'Ton pseudo, ta classe, ton étage et ton\nscore seront visibles publiquement\ndans le classement en ligne.\n\nTu peux changer d\'avis dans Paramètres.', {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY, align: 'center', lineSpacing: 4,
    }).setOrigin(0.5).setDepth(202);

    const declineBtn = this.add.text(GAME_CX - 75, GAME_CY + 75, '[ Non merci ]', {
      fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: true });

    const acceptBtn = this.add.text(GAME_CX + 75, GAME_CY + 75, '[ 🏆 Envoyer ]', {
      fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: true });

    const elements = [overlay, box, title, desc, declineBtn, acceptBtn];
    const close = () => elements.forEach(e => e.destroy());

    declineBtn.on('pointerover', () => declineBtn.setColor('#ff6b6b'));
    declineBtn.on('pointerout', () => declineBtn.setColor('#e74c3c'));
    declineBtn.on('pointerdown', () => {
      LeaderboardClient.setConsent('declined');
      this.leaderboardStatus = 'declined';
      close();
      this.drawUI();
    });

    acceptBtn.on('pointerover', () => acceptBtn.setColor('#27ae60'));
    acceptBtn.on('pointerout', () => acceptBtn.setColor('#2ecc71'));
    acceptBtn.on('pointerdown', () => {
      LeaderboardClient.setConsent('accepted');
      close();
      this.doSubmit();
    });
  }

  /** Envoie effectif au serveur — calcule le score, met à jour le status à l'écran. */
  private doSubmit(): void {
    this.leaderboardStatus = 'sending';
    this.drawUI();

    const ctx = {
      etage: this.run.getMaxFloorReached(),
      gold: this.run.getGold(),
      hpRemaining: Math.max(0, this.run.getPlayerHp()),
      enemiesKilled: this.meta.getCurrentRunKills(),
      victory: this.victory,
    };
    const score = computeRunScore(ctx);

    LeaderboardClient.submit({
      score,
      acte: 1, // Acte 1 seul implémenté pour l'instant — Phases 8/9 ajouteront 2 et 3
      etage: ctx.etage,
      classe: this.run.getSelectedClass(),
      duration: this.run.getDurationSec(),
      victory: this.victory,
    }).then((result) => {
      if (!this.scene.isActive('RunEndScene')) return;
      if (result.ok) {
        this.leaderboardStatus = 'sent';
        this.leaderboardRank = result.rank ?? null;
      } else if (result.skipped === 'declined') {
        this.leaderboardStatus = 'declined';
      } else if (result.skipped === 'no-api') {
        this.leaderboardStatus = 'no-api';
      } else {
        this.leaderboardStatus = 'failed';
      }
      this.drawUI();
    });
  }

  private drawUI(): void {
    // Nettoyer les listeners scene.input de l'ancien panel AVANT de détruire les GameObjects
    // (évite l'accumulation de listeners orphelins à chaque redessinage)
    this.panel?.destroy();
    this.panel = null;
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    const title = this.victory ? '🏆 BUTIN RÉCUPÉRÉ' : '💀 DERNIER SOUFFLE';
    const titleColor = this.victory ? '#f1c40f' : '#e74c3c';
    this.add.text(GAME_CX, 15, title, {
      fontSize: '20px', color: titleColor, fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const subtitle = this.victory
      ? 'Stocke ton équipement au coffre avant de rentrer.'
      : 'Tu peux sauver UN SEUL item. Le reste est perdu.';
    this.add.text(GAME_CX, 40, subtitle, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    const storage = this.camp.getStorage();
    this.add.text(GAME_CX, 58, `📦 Coffre : ${storage.length}/${this.camp.getStorageCapacity()}    ✓ Sauvés : ${this.savedItems.length}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Notification construction terminée
    if (this.completedConstruction) {
      const cDef = BUILDING_DEFINITIONS[this.completedConstruction.buildingId];
      this.add.text(GAME_CX, 73, `🏗️ ${cDef.emoji} ${cDef.name} amélioré → Niv.${this.completedConstruction.targetLevel} !`, {
        fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(11);
    }

    // Statut leaderboard (en victoire uniquement, juste sous le header)
    if (this.victory && this.leaderboardStatus !== 'idle' && !this.completedConstruction) {
      const status = this.getLeaderboardStatusText();
      if (status) {
        this.add.text(GAME_CX, 73, status.text, {
          fontSize: '10px', color: status.color, fontFamily: FONT_BODY,
        }).setOrigin(0.5).setDepth(11);
      }
    }

    // Bouton continuer (fixe) — ancré à droite pour rester visible mobile + desktop
    const continueBtn = this.add.text(GAME_W - 8, 12, '🏴‍☠️ Continuer', {
      fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    }).setOrigin(1, 0).setDepth(11);
    continueBtn.setInteractive({ useHandCursor: true });
    continueBtn.on('pointerdown', () => startSceneWithFade(this, 'CampScene', { camp: this.camp, meta: this.meta }));
    continueBtn.on('pointerover', () => continueBtn.setColor('#ffffff'));
    continueBtn.on('pointerout', () => continueBtn.setColor('#f1c40f'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    // Positions responsive (adapté mobile 450px / desktop 800px)
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);

    let y = 10;
    const inv = this.run.getInventory();

    // Bouton "Tout stocker" en haut du panel (visible, pas chevauche le titre)
    if (this.victory) {
      const allItems = this.getAllTransferableItems();
      const unsaved = allItems.filter(i => !this.savedItems.includes(i));
      const freeSpace = this.camp.getStorageCapacity() - this.camp.getStorage().length;
      if (unsaved.length > 0 && freeSpace >= unsaved.length) {
        const storeAllBtn = this.add.text(GAME_CX, y, `[ 📦 Tout stocker (${unsaved.length} items) ]`, {
          fontSize: '14px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
          backgroundColor: '#1a3a1a', padding: { x: 18, y: 8 },
        }).setOrigin(0.5, 0);
        storeAllBtn.setInteractive({ useHandCursor: true });
        storeAllBtn.on('pointerdown', () => {
          if (this.isProcessing) return;
          this.isProcessing = true;
          storeAllBtn.disableInteractive();
          this.sfx.select();
          for (const item of unsaved) {
            const stored = this.camp.storeItem({ uid: item.uid, definitionId: item.definitionId });
            if (stored) this.savedItems.push(item);
          }
          this.time.delayedCall(0, () => { this.drawUI(); });
          this.time.delayedCall(300, () => { this.isProcessing = false; });
        });
        storeAllBtn.on('pointerover', () => storeAllBtn.setColor('#27ae60'));
        storeAllBtn.on('pointerout', () => storeAllBtn.setColor('#2ecc71'));
        this.panel.add(storeAllBtn);
        y += 44;
      } else if (unsaved.length > 0 && freeSpace < unsaved.length) {
        // Coffre pas assez grand pour tout : indiquer pourquoi
        const lbl = this.add.text(GAME_CX, y + 4, `⚠️ Coffre trop petit pour tout stocker (${freeSpace} place(s) libre / ${unsaved.length} items à sauver)`, {
          fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY, align: 'center',
          wordWrap: { width: GAME_W - 30 },
        }).setOrigin(0.5, 0);
        this.panel.add(lbl);
        y += 26;
      }
    }

    // Équipement de TOUS les membres de l'équipe
    if (this.victory) {
      for (const member of this.run.getTeam()) {
        const equip = inv.getEquipment(member.id);
        const hasEquip = PERSONAL_SLOTS.some(s => equip[s] !== null);
        if (!hasEquip) continue;
        this.panel.add(this.add.text(lx, y, `--- ${member.name} ---`, {
          fontSize: '10px', color: '#f1c40f', fontFamily: FONT_BODY,
        }));
        y += 18;
        for (const slot of PERSONAL_SLOTS) {
          const item = equip[slot];
          if (item) {
            y = this.drawTransferItem(item, y);
            y += 6;
          }
        }
      }
    }

    // Sac à dos partagé
    const backpack = inv.getBackpack();
    if (backpack) {
      y = this.drawTransferItem(backpack, y);
      y += 6;
    }

    // Items du transport
    const transport = inv.getTransport();
    if (transport.length > 0) {
      this.panel.add(this.add.text(lx, y, `--- Sac (${transport.length} items) ---`, {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
      y += 22;
      for (const item of transport) {
        y = this.drawTransferItem(item, y);
        y += 6;
      }
    }

    // En défaite, montrer aussi l'équipé de tous les persos
    if (!this.victory && transport.length === 0 && !backpack) {
      for (const member of this.run.getTeam()) {
        const equip = inv.getEquipment(member.id);
        for (const slot of PERSONAL_SLOTS) {
          const item = equip[slot];
          if (item) {
            y = this.drawTransferItem(item, y);
            y += 6;
          }
        }
      }
    }

    // === Coffre actuel (pour retirer des items et faire de la place) ===
    const currentStorage = this.camp.getStorage();
    if (currentStorage.length > 0) {
      y += 10;
      this.panel.add(this.add.text(lx, y, `--- Coffre (${currentStorage.length}/${this.camp.getStorageCapacity()}) ---`, {
        fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
      }));
      y += 22;
      for (const sItem of [...currentStorage]) {
        const sDef = ITEM_DEFINITIONS[sItem.definitionId];
        if (!sDef) continue;
        const sColor = RARITY_COLORS[sDef.rarity] ?? '#ffffff';
        const sLabel = SLOT_LABELS[sDef.slot] ?? sDef.slot;

        const sBg = this.add.rectangle(GAME_CX, y + 10, rowW, 26, 0x162038)
          .setStrokeStyle(1, parseInt(sColor.replace('#', ''), 16));
        this.panel.add(sBg);

        this.panel.add(this.add.text(lx + 4, y + 3, `${sLabel}  ${sDef.name}`, {
          fontSize: '10px', color: sColor, fontFamily: FONT_BODY,
        }));

        const removeBtn = this.add.text(rx, y + 1, '[Jeter]', {
          fontSize: '10px', color: '#e74c3c', fontFamily: FONT_BODY,
          backgroundColor: '#2c1a1a', padding: { x: 3, y: 2 },
        }).setOrigin(1, 0);
        removeBtn.setInteractive({ useHandCursor: true });
        removeBtn.on('pointerdown', () => {
          // Confirmation obligatoire pour les items rares
          if (sDef.rarity === 'rare' && !removeBtn.getData('confirm')) {
            removeBtn.setData('confirm', true);
            removeBtn.setText('⚠️ RARE ! Confirmer ?');
            removeBtn.setStyle({ color: '#ff6b6b' });
            this.time.delayedCall(3000, () => {
              if (removeBtn.active) {
                removeBtn.setData('confirm', false);
                removeBtn.setText('[Jeter]');
                removeBtn.setStyle({ color: '#e74c3c' });
              }
            });
            return;
          }
          if (this.isProcessing) return;
          this.isProcessing = true;
          // Désactiver immédiatement pour bloquer tout ghost-click sur ce bouton
          removeBtn.disableInteractive();
          this.sfx.select();
          this.camp.withdrawItem(sItem.uid);
          // Redessiner au prochain tick
          this.time.delayedCall(0, () => { this.drawUI(); });
          // isProcessing reste true pendant 300ms : couvre les ghost-clicks mobiles
          this.time.delayedCall(300, () => { this.isProcessing = false; });
        });
        this.panel.add(removeBtn);

        y += 26;
      }
    }

    // Items déjà sauvés
    if (this.savedItems.length > 0) {
      y += 10;
      this.panel.add(this.add.text(lx, y, `--- Transféré au coffre (${this.savedItems.length}) ---`, {
        fontSize: '11px', color: '#2ecc71', fontFamily: FONT_BODY,
      }));
      y += 22;
      for (const item of this.savedItems) {
        const def = ITEM_DEFINITIONS[item.definitionId];
        if (!def) continue;
        const slotLabel = SLOT_LABELS[def.slot] ?? def.slot;
        this.panel.add(this.add.text(lx, y, `✓ ${slotLabel}  ${def.name}`, {
          fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
        }));
        y += 20;
      }
    }

    y += 40;
    this.panel.setContentHeight(y);
  }

  private drawTransferItem(item: ItemInstance, y: number): number {
    const def = ITEM_DEFINITIONS[item.definitionId];
    if (!def) return y;

    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
    const slotLabel = SLOT_LABELS[def.slot] ?? def.slot;
    // Comparaison par référence d'objet : évite qu'un item soit faussement marqué
    // comme sauvé à cause d'une collision d'uid (→ perte silencieuse de l'item).
    const alreadySaved = this.savedItems.includes(item);
    const coffrePlein = this.camp.getStorage().length >= this.camp.getStorageCapacity();
    const blocked = !this.victory && this.defeatSaved && !alreadySaved;

    const bg = this.add.rectangle(GAME_CX, y + 12, rowW, 28, alreadySaved ? 0x1a3a2a : 0x1e2d4a)
      .setStrokeStyle(1, parseInt(color.replace('#', ''), 16));
    this.panel!.add(bg);

    this.panel!.add(this.add.text(lx, y + 4, `${slotLabel}  ${def.name}`, {
      fontSize: '10px', color: alreadySaved ? '#2ecc71' : color, fontFamily: FONT_BODY,
    }));

    if (alreadySaved) {
      this.panel!.add(this.add.text(rx, y + 4, '✓', {
        fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
      }).setOrigin(1, 0));
    } else if (coffrePlein) {
      this.panel!.add(this.add.text(rx, y + 4, 'Coffre plein', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(1, 0));
    } else if (blocked) {
      this.panel!.add(this.add.text(rx, y + 4, 'Limite 1', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(1, 0));
    } else {
      const btn = this.add.text(rx, y + 2, '[Stocker]', {
        fontSize: '10px', color: '#f39c12', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 3, y: 2 },
      }).setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        // Guard anti-double tap et anti-ghost-click mobile
        if (this.isProcessing) return;
        if (this.savedItems.includes(item)) return;
        this.isProcessing = true;
        // Désactiver immédiatement ce bouton — bloque tout 2e événement sur cette instance
        btn.disableInteractive();
        this.sfx.select();
        const stored = this.camp.storeItem({ uid: item.uid, definitionId: item.definitionId });
        if (!stored) { this.isProcessing = false; return; }
        this.savedItems.push(item);
        if (!this.victory) this.defeatSaved = true;
        // Redessiner au prochain tick (évite les événements fantômes Phaser en pleine itération)
        this.time.delayedCall(0, () => { this.drawUI(); });
        // isProcessing reste true pendant 300ms : couvre les ghost-clicks mobiles
        // (touchstart → mousedown synthétique envoyé par le navigateur ~0-300ms plus tard)
        // Pendant ce cooldown, tout bouton du nouveau UI qui serait cliqué est bloqué.
        this.time.delayedCall(300, () => { this.isProcessing = false; });
      });
      this.panel!.add(btn);
    }

    return y + 28;
  }

  /** Retourne tous les items transférables (équipés sur TOUS les persos + transport + sac) */
  private getAllTransferableItems(): ItemInstance[] {
    const items: ItemInstance[] = [];
    const inv = this.run.getInventory();

    // Équipement de chaque membre de l'équipe
    for (const member of this.run.getTeam()) {
      const equip = inv.getEquipment(member.id);
      for (const slot of PERSONAL_SLOTS) {
        const item = equip[slot];
        if (item) items.push(item);
      }
    }

    // Sac à dos partagé
    const bag = inv.getBackpack();
    if (bag) items.push(bag);

    // Items dans le transport
    for (const item of inv.getTransport()) {
      items.push(item);
    }

    return items;
  }

  /** Mini-message à afficher dans le header concernant la soumission leaderboard. */
  private getLeaderboardStatusText(): { text: string; color: string } | null {
    switch (this.leaderboardStatus) {
      case 'sending':
        return { text: '🏆 Envoi du score au classement…', color: '#7f8c8d' };
      case 'sent':
        return this.leaderboardRank
          ? { text: `🏆 Score enregistré — rang #${this.leaderboardRank}`, color: '#2ecc71' }
          : { text: '🏆 Score enregistré au classement', color: '#2ecc71' };
      case 'failed':
        return { text: '⚠️ Échec envoi classement (réessayé plus tard)', color: '#e67e22' };
      case 'declined':
      case 'no-api':
      case 'idle':
        return null;
    }
  }

  shutdown(): void {
    this.panel?.destroy();
    this.panel = null;
  }
}
